import { AuthContainer, type AuthFlow } from "@/components/auth/auth-container";
import { AuthMarketingPanel, AuthMobileTrustBanner } from "@/components/auth/auth-marketing-panel";
import { AuthPageSkeleton } from "@/components/loading";
import { useSession } from "@/hooks/use-session";
import { useTRPC } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type AuthSearch = {
  flow?: "login" | "signup";
  reset?: boolean;
};

export const Route = createFileRoute("/auth")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    flow: search.flow === "signup" || search.flow === "login" ? search.flow : undefined,
    reset: search.reset === true || search.reset === "true",
  }),
});

function RouteComponent() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { sessionData, isLoading } = useSession();
  const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
  const searchParams = useSearch({ from: "/auth" });
  const [initialFlow, setInitialFlow] = useState<AuthFlow>(searchParams.flow ?? "login");

  const { data: selectedBrand } = useQuery(
    trpc.brands.getBrand.queryOptions(
      { id: selectedBrandId! },
      {
        enabled: !!sessionData && !!selectedBrandId,
      },
    ),
  );

  useEffect(() => {
    if (searchParams.reset && sessionData) {
      setInitialFlow("updatePassword");
      return;
    }

    setInitialFlow(searchParams.flow ?? "login");
  }, [searchParams.flow, searchParams.reset, sessionData]);

  useEffect(() => {
    if (sessionData && !searchParams.reset) {
      if (selectedBrand) {
        navigate({ to: "/$brand/dashboard", params: { brand: selectedBrand?.slug ?? "" } });
      } else {
        navigate({ to: "/" });
      }
    }
  }, [navigate, searchParams.reset, selectedBrand, sessionData]);

  if (isLoading) {
    return <AuthPageSkeleton />;
  }

  return (
    <div className="min-h-svh flex bg-[#f9f8f4]">
      {/* Left: Marketing / social proof panel */}
      <AuthMarketingPanel />

      {/* Right: Form panel */}
      <div className="relative flex flex-1 items-center justify-center p-6 md:p-8 lg:p-10 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile-only trust banner (lg+ shows full marketing panel instead) */}
          <AuthMobileTrustBanner />

          <AuthContainer initialFlow={initialFlow} />

          {/* Submission-layer trust indicator */}
          <div className="mt-6 flex items-center justify-center gap-2 text-[0.6875rem] text-brand-ink/30">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3 w-3"
              aria-hidden="true"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="font-medium tracking-wide">
              Secure sign-in &middot; GDPR compliant
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
